import React from "react";
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { ANALYTICS_FONT_FAMILY } from "./analyticsTypography";
import {
  DRILLDOWN_SHEET_BODY_SCROLL_SX,
  DRILLDOWN_SHEET_BORDER_RADIUS,
  DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
} from "./analyticsDrilldownStyles";

/**
 * Read-only drill-down for a single listing.
 *
 * Visually mirrors MetricDrillModal — same Dialog shell, same border-
 * radius/padding rhythm, same uppercase-caption section labels — so the
 * two modals feel like the same surface from the user's perspective.
 *
 * Everything rendered here is derived locally from the already-fetched
 * /timeseries feed events (no extra network calls). Fields whose source
 * data isn't wired up yet (preview image, listing status, days on
 * market) render as honest placeholders with a "TBD" pill.
 */

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const SectionLabel = ({ children, sx = {} }) => (
  <Typography
    variant="caption"
    sx={{
      display: "block",
      color: "rgba(0,0,0,0.5)",
      fontWeight: 600,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      fontSize: "0.65rem",
      mb: 1,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const StatTile = ({ label, value, hint }) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: "10px",
      border: "1px solid rgba(0,0,0,0.06)",
      bgcolor: "#fff",
      minWidth: 0,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "rgba(0,0,0,0.5)",
        fontSize: "0.7rem",
        fontWeight: 500,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "1.1rem",
        fontWeight: 600,
        color: "rgba(0,0,0,0.92)",
        lineHeight: 1.1,
        letterSpacing: "-0.01em",
      }}
      noWrap
    >
      {value}
    </Typography>
    {hint ? (
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "rgba(0,0,0,0.4)",
          fontSize: "0.65rem",
          mt: 0.5,
        }}
      >
        {hint}
      </Typography>
    ) : null}
  </Box>
);

const FloorPlanGlyph = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 28 28"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="3"
      width="22"
      height="22"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M3 11h22M14 3v22"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ListingDetailModal = ({ open, onClose, listing, subtitle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const refLabel = listing?.ref || "Direct upload";
  const layoutId = listing?.layoutId || null;
  const galleryUrl = listing?.galleryUrl || null;
  const uploadDate = listing?.uploadedAt;

  const stats = listing?.stats || {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: DRILLDOWN_SHEET_BORDER_RADIUS,
          ...DRILLDOWN_SHEET_PAPER_ELEVATION_SX,
          m: { xs: 0, sm: 2 },
          mt: { xs: "auto", sm: 2 },
          maxHeight: { xs: "92vh", sm: "85vh" },
          bgcolor: "#fff",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2.5, sm: 3 },
          pb: 2,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1.05rem",
                color: "rgba(0,0,0,0.9)",
                lineHeight: 1.2,
              }}
              noWrap
            >
              {refLabel}
            </Typography>
            {subtitle ? (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "rgba(0,0,0,0.5)",
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  mt: 0.25,
                }}
                noWrap
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ mt: 0, flexShrink: 0 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={DRILLDOWN_SHEET_BODY_SCROLL_SX}>
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            paddingBottom: {
              xs: "calc(env(safe-area-inset-bottom) + 24px)",
              sm: 3,
            },
          }}
        >
        <Stack spacing={3}>
          <Box>
            <SectionLabel>Preview</SectionLabel>
            <Box
              sx={{
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "rgba(0,0,0,0.03)",
                height: 140,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0,0,0,0.3)",
                position: "relative",
              }}
            >
              <FloorPlanGlyph size={40} />
              <Chip
                label="Preview coming"
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  fontSize: "0.65rem",
                  height: 22,
                  bgcolor: "rgba(0,0,0,0.05)",
                  color: "rgba(0,0,0,0.55)",
                  fontWeight: 500,
                  letterSpacing: 0.2,
                }}
              />
            </Box>
          </Box>

          <Box>
            <SectionLabel>Identifiers</SectionLabel>
            <Box
              sx={{
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              <DetailRow label="Ref">
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "rgba(0,0,0,0.85)",
                    fontFamily: ANALYTICS_FONT_FAMILY,
                  }}
                  noWrap
                >
                  {refLabel}
                </Typography>
              </DetailRow>
              <DetailRow label="Layout ID" divider>
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: layoutId ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.4)",
                    fontFamily: ANALYTICS_FONT_FAMILY,
                  }}
                  noWrap
                >
                  {layoutId || "—"}
                </Typography>
              </DetailRow>
              <DetailRow label="Gallery" divider>
                {galleryUrl ? (
                  <Box
                    component="a"
                    href={galleryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: "0.85rem",
                      color: "primary.main",
                      textDecoration: "none",
                      maxWidth: "100%",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.85rem" }}
                      noWrap
                    >
                      Open gallery
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Box>
                ) : (
                  <Typography
                    sx={{ fontSize: "0.85rem", color: "rgba(0,0,0,0.4)" }}
                  >
                    —
                  </Typography>
                )}
              </DetailRow>
              <DetailRow label="Uploaded" divider>
                <Typography
                  sx={{ fontSize: "0.85rem", color: "rgba(0,0,0,0.85)" }}
                  noWrap
                >
                  {formatTimestamp(uploadDate)}
                </Typography>
              </DetailRow>
            </Box>
          </Box>

          <Box>
            <SectionLabel>Listing context</SectionLabel>
            <Box
              sx={{
                borderRadius: "12px",
                border: "1px solid rgba(0,0,0,0.06)",
                bgcolor: "#fff",
                overflow: "hidden",
              }}
            >
              <DetailRow label="Status">
                <PlaceholderPill label="TBD" />
              </DetailRow>
              <DetailRow label="Days on market" divider>
                <PlaceholderPill label="TBD" />
              </DetailRow>
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 1,
                color: "rgba(0,0,0,0.5)",
                fontSize: "0.7rem",
                lineHeight: 1.4,
              }}
            >
              Status and days-on-market will populate once listing data is
              connected from the listing platform.
            </Typography>
          </Box>

          <Box>
            <SectionLabel>Engagement</SectionLabel>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                gap: 1,
              }}
            >
              <StatTile label="Total interactions" value={stats.total ?? 0} />
              <StatTile label="Unique Visitors" value={stats.sessions ?? 0} />
              <StatTile label="Shuffles" value={stats.shuffles ?? 0} />
              <StatTile label="Saves" value={stats.saves ?? 0} />
              <StatTile label="Shares" value={stats.shares ?? 0} />
              <StatTile
                label="Webview opens"
                value={stats.webviewOpens ?? 0}
              />
            </Box>
          </Box>
        </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};

const DetailRow = ({ label, children, divider }) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      borderTop: divider ? "1px solid rgba(0,0,0,0.06)" : "none",
      minWidth: 0,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: "rgba(0,0,0,0.5)",
        fontSize: "0.7rem",
        fontWeight: 500,
        flexShrink: 0,
        width: 96,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0, textAlign: "right" }}>{children}</Box>
  </Box>
);

const PlaceholderPill = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      px: 1,
      py: 0.25,
      bgcolor: "rgba(0,0,0,0.05)",
      borderRadius: "6px",
      fontSize: "0.7rem",
      fontWeight: 500,
      color: "rgba(0,0,0,0.55)",
      letterSpacing: 0.2,
    }}
  >
    {label}
  </Box>
);

export default ListingDetailModal;
